import React from "react";
import Select from "react-select";

/**
 * @param {Object} field - Field configuration object
 * @param {string} field.name - Unique field identifier
 * @param {string} field.label - Display label for the field
 * @param {string} field.type - Input type (text, date, select, radio, etc.)
 * @param {boolean} field.required - Whether field is required
 * @param {Array} field.options - Options for select/radio fields
 * @param {string} field.placeholder - Placeholder text for input fields
 * @param {any} value - Current field value
 * @param {Function} onChange - Callback when field value changes (name, newValue)
 * @param {string} error - Validation error message (if any)
 */
function FieldRenderer({ field, value, onChange, error, readOnly }) {
  // DESTRUCTURE FIELD CONFIGURATION
  const { name, label, type, required, options = [], placeholder = "" } = field;


  const inputClassName =
    "w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all";

  // ✅ Helper function to check if value exists in options
  const getEnhancedOptions = () => {
    if (type !== 'select' || !value) return options;

    // Check if current value exists in options
    const valueExists = options.some(opt => {
      const optValue = typeof opt === "string" ? opt : opt.value;
      return optValue === value;
    });

    // If value doesn't exist, add it as a custom option with indicator
    if (!valueExists && value) {
      const customOption = {
        label: `${value}`,
        value: value,
        isExtracted: true
      };
      return [customOption, ...options];
    }

    return options;
  };

  const enhancedOptions = getEnhancedOptions();

  // Custome styles for react-select
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "34px",
      height: "34px",
      fontSize: "0.875rem",
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
      "&:hover": {
        borderColor: "#3b82f6",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      height: "34px",
      padding: "0 6px",
    }),
    input: (base) => ({
      ...base,
      margin: "0",
      padding: "0",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "34px",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "4px",
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: "4px",
    }),
    menu: (base) => ({
      ...base,
      fontSize: "0.875rem",
      zIndex: 50,
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: "200px",
      padding: "4px",
    }),
    option: (base, state) => ({
      ...base,
      padding: "8px 12px",
      fontSize: "0.875rem",
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
          ? "#eff6ff"
          : "white",
      color: state.isSelected ? "white" : "#1f2937",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#3b82f6",
      },
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "0.875rem",
      color: "#9ca3af",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "0.875rem",
      color: "#1f2937",
    }),
    noOptionsMessage: (base) => ({
      ...base,
      fontSize: "0.875rem",
      color: "#6b7280",
      padding: "8px 12px",
    }),
  };

  return (
    <div className="space-y-1.5">
      {/* ================================================================
          LABEL WITH REQUIRED INDICATOR
      ================================================================ */}
      {type !== "checkbox" && (
        <label className="block text-xs font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}



      {/* ------------------------------------------------------------
          TEXT INPUT
      ------------------------------------------------------------ */}
      {type === "text" && (
        <input
          type="text"
          disabled={readOnly}
          value={value || ""}
          onChange={(e) => onChange(name, e.target.value)}
          placeholder={placeholder}
          className={`${inputClassName} ${readOnly ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
        />
      )}

      {/* ------------------------------------------------------------
          DATE INPUT
      ------------------------------------------------------------ */}
      {type === "date" && (
        <input
          type="date"
          disabled={readOnly}
          value={value || ""}
          onChange={(e) => onChange(name, e.target.value)}
          max={field.max || undefined}
          min={field.min || undefined}
          className={`${inputClassName} ${readOnly ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
        />
      )}

      {/* ------------------------------------------------------------
          PHONE INPUT
      ------------------------------------------------------------ */}
      {type === "phone" && (
        <input
          type="tel"
          disabled={readOnly}
          value={value || ""}
          onChange={(e) => {
            // Only allow numbers, spaces, +, -, and ()
            const sanitized = e.target.value.replace(/[^0-9+\-() ]/g, "");
            onChange(name, sanitized);
          }}
          placeholder={placeholder}
          className={`${inputClassName} ${readOnly ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
        />
      )}

      {/* ------------------------------------------------------------
          EMAIL INPUT
      ------------------------------------------------------------ */}
      {type === "email" && (
        <input
          type="email"
          disabled={readOnly}
          value={value || ""}
          onChange={(e) => onChange(name, e.target.value)}
          placeholder={placeholder}
          className={`${inputClassName} ${readOnly ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
        />
      )}

      {/* ------------------------------------------------------------
          TEXTAREA (MULTI-LINE TEXT)
      ------------------------------------------------------------ */}
      {type === "textarea" && (
        <textarea
          value={value || ""}
          disabled={readOnly}
          onChange={(e) => onChange(name, e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none transition-all"
        />
      )}

      {/* ------------------------------------------------------------
          SELECT DROPDOWN - SEARCHABLE
      ------------------------------------------------------------ */}
      {/* {type === "select" && field.searchable && (
        <Select
          value={value ? { label: value, value } : null}
          onChange={(selected) => onChange(name, selected?.value || "")}
          options={options.map((opt) =>
            typeof opt === "string" ? { label: opt, value: opt } : opt,
          )}
          placeholder={placeholder || `Select ${label}`}
          styles={customSelectStyles}
          isClearable
          isSearchable
          noOptionsMessage={() => "No options found"}
          isDisabled={readOnly}
        />
      )} */}

      {/* ------------------------------------------------------------
          SELECT DROPDOWN - STANDARD (NON-SEARCHABLE)
      ------------------------------------------------------------ */}
      {/* {type === "select" && !field.searchable && (
        <select
          value={value || ""}
          disabled={readOnly}
           placeholder={placeholder || `Select ${label}`}
          onChange={(e) => onChange(name, e.target.value)}
          className={`${inputClassName} ${
            readOnly ? "bg-gray-100 cursor-not-allowed" : ""
          }`}
        >
          
          <option value="">Select {label}</option>
          {options.map((opt) => (
            <option
              key={typeof opt === "string" ? opt : opt.value}
              value={typeof opt === "string" ? opt : opt.value}
            >
              {typeof opt === "string" ? opt : opt.label}
            </option>
          ))}
        </select>
      )} */}



      {/* ------------------------------------------------------------
          SELECT DROPDOWN - SEARCHABLE
      ------------------------------------------------------------ */}
      {type === "select" && field.searchable && (
        <Select
          value={value ?
            enhancedOptions.find(opt =>
              (typeof opt === "string" ? opt : opt.value) === value
            ) || { label: value, value, isExtracted: true }
            : null
          }
          onChange={(selected) => onChange(name, selected?.value || "")}
          options={enhancedOptions.map((opt) =>
            typeof opt === "string" ? { label: opt, value: opt } : opt,
          )}
          placeholder={placeholder || `Select ${label}`}
          styles={customSelectStyles}
          isClearable
          isSearchable
          noOptionsMessage={() => "No options found"}
          isDisabled={readOnly}
          // ✅ Allow users to type custom values
          formatCreateLabel={(inputValue) => `Use: "${inputValue}"`}
        />
      )}

      {/* ------------------------------------------------------------
          SELECT DROPDOWN - STANDARD (NON-SEARCHABLE)
      ------------------------------------------------------------ */}
      {type === "select" && !field.searchable && (
        <select
          value={value || ""}
          disabled={readOnly}
          onChange={(e) => onChange(name, e.target.value)}
          className={`${inputClassName} ${readOnly ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
        >
          <option value="">Select {label}</option>
          {enhancedOptions.map((opt) => {
            const optValue = typeof opt === "string" ? opt : opt.value;
            const optLabel = typeof opt === "string" ? opt : opt.label;
            const isExtracted = typeof opt === "object" && opt.isExtracted;

            return (
              <option
                key={optValue}
                value={optValue}
                style={{ fontStyle: isExtracted ? 'italic' : 'normal' }}
              >
                {optLabel}
              </option>
            );
          })}
        </select>
      )}



      {/* ------------------------------------------------------------
          RADIO BUTTONS (HORIZONTAL LAYOUT)
      ------------------------------------------------------------ */}
      {type === "radio" && (
        <div className="flex gap-4">
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-1.5 cursor-pointer"
            >
              <input
                type="radio"
                disabled={readOnly}
                name={name}
                value={opt.value}
                checked={value === opt.value}
                onChange={() => onChange(name, opt.value)}
                className="w-3.5 h-3.5 text-blue-600"
              />
              <span className="text-sm text-gray-700">{opt.label}</span>
            </label>
          ))}
        </div>
      )}

      {/* ------------------------------------------------------------
          CHECKBOX
      ------------------------------------------------------------ */}
      {type === "checkbox" && (
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            disabled={readOnly}
            checked={Boolean(value)}
            onChange={(e) => onChange(name, e.target.checked)}
            className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">{label}</span>
        </label>
      )}

      {/* ================================================================
          VALIDATION ERROR MESSAGE
      ================================================================ */}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

export default FieldRenderer;
